import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'

const BLUE = '#185FA5'
const GREEN = '#2e7d32'

export default function AIMatchReportModal({ fixture, squadName, clubName, onClose, currentUserEmail }) {
  const [report, setReport] = useState(fixture.match_report || null)
  const [published, setPublished] = useState(fixture.match_report_published || false)
  const [loading, setLoading] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState('')
  const [saving, setSaving] = useState(false)
  const [emailState, setEmailState] = useState(null)
  const [emailTarget, setEmailTarget] = useState(null)
  const [customEmail, setCustomEmail] = useState('')
  const [showCustomEmail, setShowCustomEmail] = useState(false)

  async function generateReport() {
    setLoading(true)
    setError(null)
    try {
      const { data: events } = await supabase
        .from('match_events')
        .select('*, players(full_name)')
        .eq('fixture_id', fixture.id)
        .order('minute', { ascending: true })

      const { data: { session } } = await supabase.auth.getSession()

      const res = await fetch('https://mrjtwgvscgcvuecxjcbg.supabase.co/functions/v1/generate-match-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + session.access_token,
        },
        body: JSON.stringify({
          fixture: {
            id: fixture.id,
            opponent: fixture.opponent,
            fixture_date: fixture.fixture_date,
            sport: fixture.sport,
            game_type: fixture.game_type,
            home_goals: fixture.home_goals,
            home_points: fixture.home_points,
            away_goals: fixture.away_goals,
            away_points: fixture.away_points,
            venue: fixture.venue,
          },
          squadName,
          clubName,
          events: (events || []).map(e => ({
            event_type: e.event_type,
            minute: e.minute,
            notes: e.notes,
            player_name: e.players?.full_name || null,
          })),
        }),
      })

      if (!res.ok) throw new Error('Failed to generate report')
      const data = await res.json()
      setReport(data.report)

      await supabase.from('fixtures')
        .update({ match_report: data.report })
        .eq('id', fixture.id)

    } catch (err) {
      setError('Failed to generate report. Please try again.')
    }
    setLoading(false)
  }

  async function togglePublish() {
    setPublishing(true)
    const newValue = !published
    await supabase.from('fixtures')
      .update({ match_report_published: newValue })
      .eq('id', fixture.id)
    setPublished(newValue)
    setPublishing(false)
  }

  async function copyToClipboard() {
    if (!report) return
    await navigator.clipboard.writeText(report)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function shareWhatsApp() {
    const text = encodeURIComponent(
      (clubName || '') + ' vs ' + fixture.opponent + ' - Match Report\n\nhttps://gaamatchtracker.ie/public/report/' + fixture.id
    )
    window.open('https://wa.me/?text=' + text, '_blank')
  }

  async function sendEmail(target) {
    setEmailTarget(target)
    setEmailState('sending')
    try {
      let recipients = []
      if (target === 'self') {
        recipients = [currentUserEmail]
      } else if (target === 'custom') {
        recipients = [customEmail.trim()]
      } else {
        const { data: members } = await supabase
          .from('squad_members')
          .select('profiles(email)')
          .eq('squad_id', fixture.squad_id)
          .eq('approved', true)
        recipients = (members || []).map(m => m.profiles?.email).filter(Boolean)
      }
      if (!recipients.length) { setEmailState('error'); return }

      const totalHome = (fixture.home_goals * 3) + fixture.home_points
      const totalAway = (fixture.away_goals * 3) + fixture.away_points
      const result = totalHome > totalAway ? 'Win' : totalHome < totalAway ? 'Loss' : 'Draw'

      const htmlBody = `
        <div style="font-family: sans-serif; max-width: 520px; margin: 0 auto;">
          <div style="background: linear-gradient(135deg, #0d3d6b 0%, #185FA5 100%); padding: 24px; border-radius: 12px 12px 0 0; text-align: center; color: white;">
            <p style="font-size: 11px; opacity: 0.7; letter-spacing: 1px; margin: 0 0 4px">MATCH REPORT</p>
            <h2 style="margin: 0 0 4px; font-size: 20px;">${squadName} vs ${fixture.opponent}</h2>
            <p style="margin: 0; opacity: 0.8; font-size: 13px;">${fixture.home_goals}-${String(fixture.home_points).padStart(2,'0')} (${totalHome}) v ${fixture.away_goals}-${String(fixture.away_points).padStart(2,'0')} (${totalAway}) — ${result}</p>
          </div>
          <div style="background: white; border: 1px solid #e0e0e0; border-top: none; border-radius: 0 0 12px 12px; padding: 20px;">
            <div style="font-size: 14px; line-height: 1.8; color: #333; white-space: pre-wrap;">${report}</div>
            <p style="font-size: 11px; color: #bbb; text-align: center; margin-top: 20px;">GAA Match Tracker · gaamatchtracker.ie</p>
          </div>
        </div>`

      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('https://mrjtwgvscgcvuecxjcbg.supabase.co/functions/v1/send-invite-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + session.access_token },
        body: JSON.stringify({
          type: 'match_report',
          toEmails: recipients,
          subject: `Match Report: ${squadName} vs ${fixture.opponent} — ${result}`,
          htmlBody,
        }),
      })
      if (!res.ok) throw new Error('Failed')
      setEmailState('sent')
    } catch (e) {
      setEmailState('error')
    }
  }

  const totalHome = (fixture.home_goals * 3) + fixture.home_points
  const totalAway = (fixture.away_goals * 3) + fixture.away_points
  const result = totalHome > totalAway ? 'Win' : totalHome < totalAway ? 'Loss' : 'Draw'

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>

        <div style={{ background: 'linear-gradient(135deg, #0d3d6b 0%, #185FA5 100%)', color: '#fff', padding: '20px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 11, opacity: 0.7, letterSpacing: 1, marginBottom: 4 }}>AI MATCH REPORT</div>
              <div style={{ fontSize: 17, fontWeight: 700 }}>{squadName} vs {fixture.opponent}</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                {fixture.home_goals}-{String(fixture.home_points).padStart(2, '0')} ({totalHome})  v  {fixture.away_goals}-{String(fixture.away_points).padStart(2, '0')} ({totalAway}) — {result}
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', opacity: 0.7 }}>×</button>
          </div>
        </div>

        <div style={{ padding: 16 }}>
          {error && (
            <div style={{ background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#c0392b', fontSize: 14 }}>
              {error}
            </div>
          )}

          {!report && !loading && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📰</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#333', marginBottom: 8 }}>Generate Match Report</div>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 24 }}>
                AI will write a newspaper-style match report based on the recorded events from this game.
              </div>
              <button
                onClick={generateReport}
                style={{ padding: '12px 28px', background: BLUE, color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}
              >
                ✨ Generate Report
              </button>
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✍️</div>
              <div style={{ fontSize: 14, color: '#888' }}>Writing match report...</div>
            </div>
          )}

          {report && !loading && (
            <div>
              <div style={{ background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, marginBottom: 16, fontSize: 14, lineHeight: 1.8, color: '#333', whiteSpace: 'pre-wrap' }}>
                {report}
              </div>

              <div style={{ background: published ? '#e8f5e9' : '#f5f5f5', border: '1px solid ' + (published ? '#81c784' : '#ddd'), borderRadius: 10, padding: '12px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: published ? GREEN : '#555' }}>
                    {published ? '✅ Published to public page' : '🔒 Not published'}
                  </div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                    {published ? 'Parents and supporters can read this report' : 'Only visible to coaches'}
                  </div>
                </div>
                <button
                  onClick={togglePublish}
                  disabled={publishing}
                  style={{ padding: '8px 16px', background: published ? '#fff' : BLUE, color: published ? '#c62828' : '#fff', border: '1px solid ' + (published ? '#e57373' : BLUE), borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, opacity: publishing ? 0.6 : 1 }}
                >
                  {publishing ? '...' : published ? 'Unpublish' : 'Publish'}
                </button>
              </div>

{editing ? (
                <div style={{ marginBottom: 16 }}>
                  <textarea
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    rows={12}
                    style={{ width: '100%', padding: 12, border: '1px solid #ddd', borderRadius: 8, fontSize: 13, lineHeight: 1.7, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button
                      onClick={async () => {
                        setSaving(true)
                        await supabase.from('fixtures').update({ match_report: editText }).eq('id', fixture.id)
                        setReport(editText)
                        setEditing(false)
                        setSaving(false)
                      }}
                      disabled={saving}
                      style={{ flex: 2, padding: '10px', background: BLUE, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, opacity: saving ? 0.6 : 1 }}
                    >
                      {saving ? 'Saving...' : '💾 Save changes'}
                    </button>
                    <button
                      onClick={() => setEditing(false)}
                      style={{ flex: 1, padding: '10px', background: '#f0f0f0', color: '#333', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <button onClick={copyToClipboard} style={{ flex: 1, padding: '10px', background: copied ? '#e8f5e9' : '#f0f0f0', color: copied ? GREEN : '#333', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                    {copied ? '✅ Copied!' : '📋 Copy'}
                  </button>
                  <button onClick={shareWhatsApp} style={{ flex: 1, padding: '10px', background: '#e8f5e9', color: GREEN, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                    💬 WhatsApp
                  </button>
                  <button onClick={() => { setEditText(report); setEditing(true) }} style={{ flex: 1, padding: '10px', background: '#fff8e1', color: '#e65100', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                    ✏️ Edit
                  </button>
                  <button onClick={generateReport} style={{ flex: 1, padding: '10px', background: '#f0f5ff', color: BLUE, border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                    🔄 Regenerate
                  </button>
                </div>
              )}

              <div style={{ background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#888', letterSpacing: 0.5, marginBottom: 10 }}>EMAIL REPORT</div>
                {emailState === 'sent' ? (
                  <p style={{ fontSize: 13, color: GREEN, textAlign: 'center', padding: 8 }}>✅ Report emailed successfully</p>
                ) : emailState === 'error' ? (
                  <p style={{ fontSize: 13, color: '#c0392b', textAlign: 'center', padding: 8 }}>❌ Failed to send. Please try again.</p>
                ) : emailState === 'sending' ? (
                  <p style={{ fontSize: 13, color: '#888', textAlign: 'center', padding: 8 }}>Sending...</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button onClick={() => sendEmail('self')} style={{ padding: '10px', background: '#E6F1FB', color: BLUE, border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                      📧 Email to myself
                    </button>
                    <button onClick={() => sendEmail('all')} style={{ padding: '10px', background: BLUE, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                      📨 Email to all coaches
                    </button>
                    {showCustomEmail ? (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input
                          type="email"
                          placeholder="Enter email address"
                          value={customEmail}
                          onChange={e => setCustomEmail(e.target.value)}
                          style={{ flex: 1, padding: '9px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, outline: 'none' }}
                        />
                        <button
                          onClick={() => sendEmail('custom')}
                          disabled={!customEmail.includes('@')}
                          style={{ padding: '9px 14px', background: GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: !customEmail.includes('@') ? 0.5 : 1 }}
                        >
                          Send
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => setShowCustomEmail(true)} style={{ padding: '10px', background: '#f0f0f0', color: '#555', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                        ✉️ Email to specific address
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <button onClick={onClose} style={{ width: '100%', padding: 12, background: BLUE, color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}